import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { MatIconModule } from '@angular/material/icon';

import { StatusTrackerBlock } from '@core/services/ai/ai-assistant.service';

@Component({
  selector: 'app-ai-status-tracker-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule],
  template: `
    @if (block.title) {
      <div class="st__title">{{ block.title }}</div>
    }
    <ol class="st" [attr.data-tone]="block.tone || 'neutral'">
      @for (s of block.steps; track s.label; let i = $index) {
        <li
          class="st__step"
          [class.st__step--done]="i < block.currentIndex"
          [class.st__step--current]="i === block.currentIndex"
          [class.st__step--upcoming]="i > block.currentIndex">
          <div class="st__dot">
            @if (i < block.currentIndex) {
              <mat-icon>check</mat-icon>
            } @else if (s.icon) {
              <mat-icon>{{ s.icon }}</mat-icon>
            } @else {
              <span class="st__dot-num">{{ i + 1 }}</span>
            }
          </div>
          <div class="st__body">
            <div class="st__label">{{ s.label }}</div>
            @if (s.sub) {
              <div class="st__sub">{{ s.sub }}</div>
            }
          </div>
          @if (i < block.steps.length - 1) {
            <div class="st__connector"></div>
          }
        </li>
      }
    </ol>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .st__title {
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgb(var(--ds-text) / 0.55);
        margin: 0 0 8px;
      }
      .st {
        list-style: none;
        margin: 0;
        padding: 12px 14px;
        background: rgb(var(--ds-surface));
        border: 1px solid rgb(var(--ds-text) / 0.08);
        border-radius: 12px;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        gap: 0;
        overflow-x: auto;
      }
      .st__step {
        flex: 1 1 0;
        /* Let steps shrink to share the available width evenly instead of
         forcing a fixed 110px each — 4+ steps at 110px overflowed the narrow
         panel and triggered a horizontal scrollbar that clipped the last step.
         The 28px dot (flex-shrink: 0) keeps steps from collapsing too far, and
         labels wrap onto a second line when space is tight. */
        min-width: 0;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        padding: 0 6px;
      }
      .st__dot {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: rgb(var(--ds-surface));
        border: 2px solid rgb(var(--ds-text) / 0.15);
        color: rgb(var(--ds-text) / 0.5);
        display: grid;
        place-items: center;
        font-weight: 700;
        font-size: 12px;
        z-index: 1;
        flex-shrink: 0;
      }
      .st__dot mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
      }
      .st__step--done .st__dot {
        background: rgb(var(--ds-interactive));
        border-color: rgb(var(--ds-interactive));
        color: rgb(var(--ds-surface));
      }
      .st__step--current .st__dot {
        background: rgb(var(--ds-surface));
        border-color: rgb(var(--ds-interactive));
        color: rgb(var(--ds-interactive));
        box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.18);
      }
      .st[data-tone='warning'] .st__step--current .st__dot {
        border-color: rgb(var(--ds-warning));
        color: rgb(var(--ds-warning));
        box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.2);
      }
      .st[data-tone='danger'] .st__step--current .st__dot {
        border-color: rgb(var(--ds-danger));
        color: rgb(var(--ds-danger));
        box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.2);
      }
      .st[data-tone='positive'] .st__step--done .st__dot,
      .st[data-tone='positive'] .st__step--current .st__dot {
        background: rgb(var(--ds-success));
        border-color: rgb(var(--ds-success));
        color: rgb(var(--ds-surface));
        box-shadow: none;
      }
      .st__body {
        text-align: center;
        max-width: 100%;
      }
      .st__label {
        font-size: 12px;
        font-weight: 600;
        color: rgb(var(--ds-text) / 0.85);
        overflow-wrap: anywhere;
      }
      .st__step--upcoming .st__label {
        color: rgb(var(--ds-text) / 0.5);
        font-weight: 500;
      }
      .st__sub {
        font-size: 11px;
        color: rgb(var(--ds-text) / 0.55);
        margin-top: 2px;
        overflow-wrap: anywhere;
      }
      .st__connector {
        position: absolute;
        top: 14px;
        left: calc(50% + 14px);
        right: calc(-50% + 14px);
        height: 2px;
        background: rgb(var(--ds-text) / 0.12);
        z-index: 0;
      }
      .st__step--done + .st__step .st__connector,
      .st__step--done .st__connector {
        background: rgb(var(--ds-interactive));
      }
      .st[data-tone='positive'] .st__step--done .st__connector {
        background: rgb(var(--ds-success));
      }
    `
  ]
})
export class AiStatusTrackerBlockComponent {
  @Input({ required: true }) block!: StatusTrackerBlock;
}
